import { BaseQueryApi, createApi, FetchArgs, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import queryString from 'query-string';
import axios from 'axios';
import { store } from '../store';
import { actions } from '../ducks/auth';
import { toast } from 'react-toastify';
import { CustomErrorToast } from '@shared/components/customErrorToast';

const EXPIRY_SKEW_SECONDS = 30;

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

const toastIgnoreList: { uri: string; status: number[]; method?: string }[] = [
  { uri: '/sip-trunk', status: [404] },
  { uri: '/reports', status: [400, 403, 404, 500] },
  { uri: '/agent-profile', status: [409] },
  { uri: 'sign-in', status: [400, 401, 404] },
  { uri: '/admin/companies/', status: [409] },
  { uri: '/companies/', status: [404, 409] },
  { uri: '/v2/campaigns/', status: [400], method: 'PATCH' },
];

const getRequestUrl = (args: string | FetchArgs): string =>
  typeof args === 'string' ? args : args.url;

const isSignInRequest = (url: string): boolean =>
  url.includes('/v1/user/sign-in') || url.includes('/v1/admin/sign-in');

const isSuperAdminContext = () => (window.location.href || '').includes('super-admin');

const getStoredAccessToken = (): string | null =>
  localStorage.getItem(isSuperAdminContext() ? 'superAdminAccessToken' : 'accessToken');

const getExpirySeconds = (token: string): number | null => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;

    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(atob(base64)) as { exp?: number };

    return typeof exp === 'number' ? exp : null;
  } catch {
    return null;
  }
};

const isExpired = (token: string): boolean => {
  const exp = getExpirySeconds(token);
  if (exp === null) return false;

  return exp - EXPIRY_SKEW_SECONDS <= Date.now() / 1000;
};

export async function refreshToken(): Promise<string | null> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshPromise = (async () => {
      try {
        const superAdmin = isSuperAdminContext();
        const refreshEndpoint = superAdmin ? '/v1/admin/refresh-token' : '/v1/user/refresh-token';

        // refreshToken lives in an HttpOnly cookie - the browser attaches it automatically
        const refreshResponse = await axios.post(
          `${import.meta.env.VITE_API_URL}${refreshEndpoint}`,
          {},
          { withCredentials: true },
        );

        const { accessToken } = refreshResponse.data;

        if (superAdmin) {
          store.dispatch(actions.signInSuperAdmin({ accessToken }));
        } else {
          store.dispatch(actions.signIn({ accessToken }));
        }

        return accessToken;
      } catch (error: unknown) {
        console.error('Error refreshing token:', error);
        toast.error('Session expired. Please log in again.');

        store.dispatch(actions.signOut());
        return null;
      } finally {
        isRefreshing = false;
        refreshPromise = null;
      }
    })();
  }

  return refreshPromise;
}

export const ensureFreshAccessToken = async (): Promise<string | null> => {
  const token = getStoredAccessToken();

  if (token && !isExpired(token)) return token;

  return refreshToken();
};

export const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URL,
  credentials: 'include',
  prepareHeaders: (headers) => {
    const token = getStoredAccessToken();

    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }

    return headers;
  },
  paramsSerializer: (params) => queryString.stringify(params),
});

export const baseQueryWithReauth = async (
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: object,
) => {
  let result = await baseQuery(args, api, extraOptions);
  const signInRequest = isSignInRequest(getRequestUrl(args));

  if (result.error && result.error.status === 401 && !signInRequest) {
    const newAccessToken = await refreshToken();

    if (newAccessToken) {
      const newHeaders = new Headers();
      newHeaders.set('authorization', `Bearer ${newAccessToken}`);

      if (typeof args === 'string') {
        result = await baseQuery({ url: args, headers: newHeaders }, api, extraOptions);
      } else {
        result = await baseQuery(
          { ...args, headers: { ...args.headers, authorization: `Bearer ${newAccessToken}` } },
          api,
          extraOptions,
        );
      }
    }
  }

  if (result.error) {
    const errorMessage =
      result.error.data && typeof result.error.data === 'object' && 'message' in result.error.data
        ? (result.error.data as { message: string }).message
        : 'An error occurred';

    const requestUrl = result.meta?.request.url || getRequestUrl(args);
    const requestMethod = result.meta?.request.method || '';
    const errorStatus = typeof result.error.status === 'number' ? result.error.status : 0;

    const shouldIgnoreToast = toastIgnoreList.some(
      (exception) =>
        requestUrl.includes(exception.uri) &&
        exception.status.includes(errorStatus) &&
        (!exception.method || exception.method === requestMethod),
    );

    if (!signInRequest && !shouldIgnoreToast) {
      toast.error((toastProps) => <CustomErrorToast {...toastProps} message={errorMessage} />, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
      });
    }
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: 'baseApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Campaign', 'CompanyOverview'],
  endpoints: () => ({}),
});

export type GetCampaignReportResponse = { blob: Blob; contentDisposition: string | null };

baseApi.injectEndpoints({
  endpoints: (build) => ({
    getCampaignReport: build.query<
      GetCampaignReportResponse,
      { campaignId: string; queryParams: string }
    >({
      query: ({ campaignId, queryParams }) => ({
        url: `/v2/campaigns/${campaignId}/reports${queryParams}`,
        responseHandler: async (response) => {
          const blob = await response.blob();
          const contentDisposition = response.headers.get('Content-Disposition');
          return { blob, contentDisposition };
        },
      }),
    }),
  }),
});

type GetCampaignReportArgs = { campaignId: string; queryParams: string };

export const useLazyGetCampaignReportQuery = (
  baseApi as unknown as {
    useLazyGetCampaignReportQuery: () => [
      (arg: GetCampaignReportArgs) => Promise<{
        data?: GetCampaignReportResponse;
        error?: unknown;
        isSuccess: boolean;
        isError: boolean;
      }>,
      { data?: GetCampaignReportResponse; error?: unknown; isSuccess: boolean; isError: boolean },
    ];
  }
).useLazyGetCampaignReportQuery;
